import axios from "axios";

export default class ManagerSubscriptionsApiClient {
    constructor() {
        this.basePath = process.env.REACT_APP_API_BASE_PATH.replace(/\/+$/, '');
    }

    async getStatus(token) {
        const url = `${this.basePath}/manager-subscriptions/status`;
        try {
            const response = await axios.get(url, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': token
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.getStatus: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore di connessione' };
        }
    }

    async verifyDiscount(token, code) {
        const url = `${this.basePath}/manager-subscriptions/verify-discount`;
        try {
            const response = await axios.post(url, { code }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': token
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.verifyDiscount: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore di connessione' };
        }
    }

    async checkout(token, discountCode, paymentMethod) {
        const url = `${this.basePath}/manager-subscriptions/checkout`;
        const payload = {};
        if (discountCode) payload.discountCode = discountCode;
        if (paymentMethod) payload.paymentMethod = paymentMethod;
        try {
            const response = await axios.post(url, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': token
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.checkout: ', error);
            if (error.response && error.response.status === 409) {
                const detail = error.response.data?.detail || '';
                if (detail.includes('Pending bank transfer')) return { error: 'PendingBankTransfer' };
                return { error: 'Conflict' };
            }
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore di connessione' };
        }
    }

    async saveBillingData(token, data) {
        const url = `${this.basePath}/manager-subscriptions/billing-data`;
        try {
            const response = await axios.post(url, data, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': token
                }
            });
            return response.status === 204 ? true : { error: 'Errore imprevisto' };
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.saveBillingData: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore di connessione' };
        }
    }

    async getTransactions(token) {
        const url = `${this.basePath}/manager-subscriptions/transactions`;
        try {
            const response = await axios.get(url, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': token
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.getTransactions: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore di connessione' };
        }
    }

    async downloadInvoicePdf(token, id) {
        const url = `${this.basePath}/manager-subscriptions/transactions/${id}/invoice/pdf`;
        try {
            const response = await axios.get(url, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': token
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.downloadInvoicePdf: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore durante il download' };
        }
    }

    async downloadInvoiceXml(token, id) {
        const url = `${this.basePath}/manager-subscriptions/transactions/${id}/invoice/xml`;
        try {
            const response = await axios.get(url, {
                headers: {
                    'Authorization': token
                },
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.downloadInvoiceXml: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore durante il download xml' };
        }
    }

    async updateBillingData(token, data) {
        const url = `${this.basePath}/manager-subscriptions/billing-data`;
        try {
            const response = await axios.put(url, data, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': token
                }
            });
            return response.status === 204 ? true : { error: 'Errore imprevisto' };
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.updateBillingData: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore di connessione' };
        }
    }

    // ADMIN
    async getAdminDashboard(token) {
        const url = `${this.basePath}/manager-subscriptions/admin/dashboard`;
        try {
            const response = await axios.get(url, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': token
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.getAdminDashboard: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore di connessione' };
        }
    }

    async getAdminSubscriptions(token, params = {}) {
        const url = `${this.basePath}/manager-subscriptions/admin/subscriptions`;
        try {
            const response = await axios.get(url, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': token
                },
                params
            });
            return response.data;
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.getAdminSubscriptions: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore di connessione' };
        }
    }

    async updateAdminSubscription(token, id, data) {
        const url = `${this.basePath}/manager-subscriptions/admin/subscriptions/${id}`;
        try {
            const response = await axios.patch(url, data, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': token
                }
            });
            return response.status === 204 ? true : { error: 'Errore imprevisto' };
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.updateAdminSubscription: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore di connessione' };
        }
    }

    async sdiResendInvoiceAdmin(token, id) {
        const url = `${this.basePath}/manager-subscriptions/admin/subscriptions/${id}/sdi/resend`;
        try {
            const response = await axios.post(url, {}, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': token
                }
            });
            return response.status === 204 ? true : { error: 'Errore imprevisto' };
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.sdiResendInvoiceAdmin: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore di connessione' };
        }
    }

    async getAdminSettings(token) {
        const url = `${this.basePath}/manager-subscriptions/admin/settings`;
        try {
            const response = await axios.get(url, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': token
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.getAdminSettings: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore di connessione' };
        }
    }

    async cancelRenewal(token) {
        const url = `${this.basePath}/manager-subscriptions/cancel-renewal`;
        try {
            const response = await axios.post(url, {}, {
                headers: { 'Accept': 'application/json', 'Authorization': token }
            });
            return response.status === 204 ? true : { error: 'Errore imprevisto' };
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.cancelRenewal: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore di connessione' };
        }
    }

    async reactivateRenewal(token) {
        const url = `${this.basePath}/manager-subscriptions/reactivate-renewal`;
        try {
            const response = await axios.post(url, {}, {
                headers: { 'Accept': 'application/json', 'Authorization': token }
            });
            return response.status === 204 ? true : { error: 'Errore imprevisto' };
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.reactivateRenewal: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore di connessione' };
        }
    }

    async createFreeAdminSubscription(token, data) {
        const url = `${this.basePath}/manager-subscriptions/admin/subscriptions/free`;
        try {
            const response = await axios.post(url, data, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': token
                }
            });
            return response.status === 204 || response.status === 201 ? true : { error: 'Errore imprevisto' };
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.createFreeAdminSubscription: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore di connessione' };
        }
    }

    async updateAdminSettings(token, data) {
        const url = `${this.basePath}/manager-subscriptions/admin/settings`;
        try {
            const response = await axios.put(url, data, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': token
                }
            });
            return response.status === 204 ? true : { error: 'Errore imprevisto' };
        } catch (error) {
            console.error('Error in ManagerSubscriptionsApiClient.updateAdminSettings: ', error);
            if (error.response) return { error: error.response.data?.detail || 'Errore generico' };
            return { error: 'Errore di connessione' };
        }
    }
}
